import { useFilters } from './custom/useFilters';
import { useBFF } from '~/composables/useBFF/useBFF';
import type { Category } from '~/types/GraphQL';

export type UseCategory = Category & {
  id: string;
  orderHint: string;
  description: string;
  name: string;
  slug: string;
  slugAllLocales: Record<string, string>;
  canonicalUrl: string;
  key: string;
  metaTitle: string;
  parent: {
    slugAllLocales: Record<string, string>;
  };
  metaDescription: string;
  headlineTitle: string;
  isHidden: boolean;
  hideChartSize: boolean;
  childrenItems: Category[];
  saleMetadata: {
    metaTitle?: string;
    metaDescription?: string;
    headline?: string;
    description?: string;
  };
  redirectionRules?: {
    statusCode: number;
    redirectUrl: string;
  };
};

const loading = ref(false);

const useCategory = () => {
  const { $categories } = useBFF();
  const { $config, $domain, $i18n, runWithContext } = useNuxtApp();
  const result = useStateCategory();
  const { headlineColorSelected } = useFilters();
  const route = useRoute();
  const { result: categories, load: loadCategories, flatCategories, rootCategory } = useCategories();

  const {
    slug_1: slug1,
    slug_2: slug2,
    slug_3: slug3,
    slug_4: slug4,
  } = route.params;
  const { isSale, isNew } = route.meta;

  const load = async () => {
    const [slug] = [slug4, slug3, slug2, slug1].filter(item => !item?.includes('c_')).filter(Boolean);

    if (!categories.value) {
      await loadCategories();
    }

    result.value = undefined;

    if (!slug) { // not regular category page
      if (!isSale && !isNew) { // not sale or new, which means it's a magnolia page so no need to load category info
        return;
      }

      loading.value = true;

      try {
        const { data: category, errors } = await $categories.getCategory({ key: $config.public.category?.rootKey, excludeChildren: true });

        if (errors.length) {
          throw new Error(errors.join(''));
        }

        result.value = category as UseCategory;
      }
      finally {
        loading.value = false;
      }
      return;
    }

    const customCategories = {
      ...flatCategories.value,
      [rootCategory.value.slug]: rootCategory.value.key,
    };
    const key = customCategories[slug as string];
    if (!key) {
      navigateTo('/', { redirectCode: 301 });
      return;
    }

    if (key === $config.public.category?.rootKey) {
      return;
    }

    loading.value = true;
    try {
      const { data: category, errors } = await $categories.getCategory({ key });
      if (errors.length) {
        throw new Error(`Category not found for key '${key}'`);
      }

      result.value = category as UseCategory;
    }
    finally {
      loading.value = false;
    }
  };

  const getCategoryName = (category: Category | undefined, route: any, i18n: any): string => {
    if (route.meta.isNew) {
      return `${category?.metaTitle ?? category?.name} | ${i18n.t('New Collection')}`;
    }

    if (route.meta.isSale) {
      return category?.saleMetadata?.metaTitle || `${i18n.t('Sale')}: ${category?.metaTitle ?? category?.name}`;
    }

    return category?.metaTitle || '';
  };

  const getCategoryDescription = (category: Category | undefined, route: any): string | null => {
    if (route.meta.isNew) {
      return null;
    }

    if (route.meta.isSale) {
      return category?.saleMetadata?.metaDescription || null;
    }

    return category?.metaDescription || null;
  };

  const getTitle = (params: {
    category?: Category;
    isSearch: boolean;
    headlineColor?: string;
    query: any;
    i18n: any;
  }): string => {
    const { category, isSearch, headlineColor, query, i18n } = params;
    if (!category) {
      if (isSearch) {
        return i18n.t('Search results for {term} | KiK Onlineshop', { term: query.search });
      }
      return i18n.t('All new products at KiK | New products at KiK');
    }
    if (route.meta.isNew) {
      return category.key === 'ShopNavigation' ? `${i18n.t('New Collection')}` : `${category.name} | ${i18n.t('New Collection')}`;
    }
    if (route.meta.isSale) {
      return category.key === 'ShopNavigation' ? category?.saleMetadata?.headline as string : `${category.name} | ${i18n.t('Sale')}`;
    }
    if (headlineColor) {
      return `${category.headlineTitle} in ${headlineColor} ${i18n.t('buy cheap')}`;
    }

    if (category.metaTitle) {
      return category.metaTitle;
    }

    return `${category.name} | ${i18n.t('Great offers now on KiK')}`;
  };

  const getDescription = (params: {
    category?: Category;
    currentDescription: string | null;
    i18n: any;
  }): string => {
    const { category, currentDescription, i18n } = params;
    const greatOffers = i18n.t('Great offers now on KiK');

    if (!category) {
      return i18n.t('New products in Kik | Always buy cheap on the online store | Great deals now on KiK');
    }

    if (currentDescription || route.meta.isNew) {
      return currentDescription ?? '';
    }

    return `${category.name} ✓ ${i18n.t('Always shop cheaply in the online shop')} | ${greatOffers}`;
  };

  const getMetaTags = (params: {
    headlineColor?: string;
    category?: Category;
    defaultContent: string;
    description: string;
    shopUrl: string;
    fullPath: string;
    i18n: any;
  }) => {
    const { headlineColor, category, defaultContent, description, shopUrl, fullPath, i18n } = params;
    const meta = [
      {
        hid: 'apple-mobile-web-app-title',
        name: 'apple-mobile-web-app-title',
        property: 'apple-mobile-web-app-title',
        content: getMetaContent(headlineColor, category, defaultContent, i18n),
      },
      {
        hid: 'og:title',
        property: 'og:title',
        name: 'og:title',
        content: getMetaContent(headlineColor, category, defaultContent, i18n),
      },
      {
        hid: 'description',
        property: 'description',
        name: 'description',
        content: getMetaDescription(headlineColor, category, description, i18n),
      },
      {
        hid: 'og:description',
        property: 'og:description',
        name: 'og:description',
        content: getMetaDescription(headlineColor, category, description, i18n),
      },
    ];

    if (!category) {
      meta.push({
        hid: 'og:url',
        property: 'og:url',
        content: `${shopUrl}${fullPath}`,
        name: 'og:url',
      });
    }

    return meta;
  };

  const getNavigationLinks = (path: string, currentPage: number) => {
    const links = [{
      rel: 'next',
      href: `${path}?page=${Number.isNaN(currentPage) ? 2 : currentPage + 1}`,
    }];

    if (currentPage > 1) {
      links.push({
        rel: 'prev',
        href: `${path}?page=${currentPage - 1}`,
      });
    }

    return links;
  };

  const getMetaContent = (headlineColor: string | undefined, category: Category | undefined, defaultContent: string, i18n: any): string => {
    if (!headlineColor || !category?.headlineTitle) {
      return defaultContent;
    }

    return `${category.headlineTitle} in ${headlineColor} ${i18n.t('buy cheap')}`;
  };

  const getMetaDescription = (headlineColor: string | undefined, category: Category | undefined, description: string, i18n: any): string => {
    if (!headlineColor || !category?.headlineTitle) {
      return description;
    }

    return `${category.headlineTitle} in ${headlineColor} ${i18n.t('at unbeatable prices ✓ Discover a wide selection of styles at KiK ✓ Shop now at KiK')}`;
  };

  const setCategoryMeta = (category?: Category) => runWithContext(() => {
    const { query, path, fullPath } = route;
    const { host: shopUrl } = $domain;

    const currentCategoryName = getCategoryName(category, route, $i18n);
    const currentCategoryDescription = getCategoryDescription(category, route);
    const defaultContent = category
      ? `${currentCategoryName} | KiK Onlineshop`
      : $i18n.t('All new products at KiK | New products at KiK');

    const title = getTitle({
      category,
      isSearch: Boolean(route.meta.isSearch),
      headlineColor: headlineColorSelected.value,
      query,
      i18n: $i18n,
    });

    const description = getDescription({
      category,
      currentDescription: currentCategoryDescription,
      i18n: $i18n,
    });

    const meta = getMetaTags({
      headlineColor: headlineColorSelected.value,
      category,
      defaultContent,
      description,
      shopUrl,
      fullPath,
      i18n: $i18n,
    });

    const link = getNavigationLinks(path, Number(query.page));

    useHead({
      title,
      meta,
      link,
    });
  });

  return {
    result: computed(() => result.value),
    loading: computed(() => loading.value),

    setCategoryMeta,
    load,
  };
};

export { useCategory };
